import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import { ArrowRightIcon, Button, type ButtonProps } from '@blade/ui-kit'
import { InsufficientBalanceLightError } from '@root/features/trade/validation-errors'
import { RectangleNetworkBackground } from '@root/shared/ui'

import {
  InsufficientAllocatedAmount,
  NoTargetsSelected,
  NoWalletSelected,
  reatomDistributeFunds,
} from '../atoms'

import { WalletDistributeGraph } from './wallet-distribute-graph'

import type { DistributeFundsModel } from '../atoms'
import type { WalletModel } from '@root/entities/wallet/atoms'

type Props = {
  model?: DistributeFundsModel
  sourceWallet?: WalletModel
  onClose?: () => void
}

export const WalletDistribute: React.FC<Props> = ({
  model: initialModel,
  sourceWallet,
  onClose,
}) => {
  const { t } = useTranslation()
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => initialModel ?? reatomDistributeFunds({ sourceWallet }),
    [initialModel, sourceWallet],
  )

  const [validationError] = useAtom(model.validationError)
  const [requestState] = useAtom(model.requestState)
  const execute = useAction(model.requestState.execute)
  const initialize = useAction(model.initialize)

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onClose?.()
    }
  }, [requestState, onClose])

  useEffect(() => {
    initialize()
  }, [initialize])

  const buttonProps = useMemo((): Partial<ButtonProps> => {
    if (!validationError) {
      return {
        children: t('wallet.distribute'),
        disabled: false,
        iconRight: <ArrowRightIcon />,
      }
    }

    const label = match(validationError)
      .with(P.instanceOf(NoWalletSelected), () =>
        t('wallet.distribute.error.no-wallet'),
      )
      .with(P.instanceOf(InsufficientBalanceLightError), () =>
        t('wallet.distribute.error.insufficient-balance'),
      )
      .with(P.instanceOf(NoTargetsSelected), () =>
        t('wallet.distribute.error.no-targets'),
      )
      .with(P.instanceOf(InsufficientAllocatedAmount), () =>
        t('wallet.distribute.error.insufficient-balance'),
      )
      .exhaustive()

    return {
      children: label,
      disabled: true,
    }
  }, [validationError, t])

  return (
    <Root>
      <Title>{t('wallet.distribute.header-description')}</Title>
      <WalletDistributeGraph model={model} />
      <ButtonContainer>
        <ButtonStyled
          {...buttonProps}
          busy={AsyncStateN.isLoading(requestState)}
          size="h36"
          radius="rounded"
          variant="solid"
          color="primary"
          onClick={execute}
        >
          {buttonProps.children}
        </ButtonStyled>
      </ButtonContainer>
    </Root>
  )
}

const Title = styled.div`
  color: var(--text-10);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;

  margin-bottom: 14px;
`
const Root = styled.div`
  ${RectangleNetworkBackground}
  border-radius: 12px;
  margin-top: 12px;
  width: 854px;
  min-height: 500px;
  padding-block: 40px 24px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 10px;
`

const ButtonContainer = styled.div`
  display: grid;
  justify-content: center;
`

const ButtonStyled = styled(Button)`
  width: 340px;
`
